import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Shield, Target, Award, Users, BarChart3 } from 'lucide-react';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: TrendingUp,
      title: 'Data-Driven Insights',
      description: 'No guesswork. Every recommendation backed by 7+ years of market research and analysis.'
    },
    {
      icon: Shield,
      title: 'Complete Transparency',
      description: 'Honest communication, realistic projections. We build trust through integrity, not false promises.'
    },
    {
      icon: Target,
      title: 'Strategic Focus',
      description: 'Dubai\'s most promising locations identified through rigorous analysis of growth patterns and infrastructure.'
    },
    {
      icon: Award,
      title: 'Premium Partnerships',
      description: 'Exclusive access to projects from Dubai\'s top-tier developers - EMAAR, DAMAC, NAKHEEL, and more.'
    },
    {
      icon: Users,
      title: 'Global Network',
      description: '100+ successful investors across multiple countries trust us with their Dubai property investments.'
    },
    {
      icon: BarChart3,
      title: 'Proven Track Record',
      description: '500+ properties delivered with consistent performance exceeding client expectations.'
    },
  ];

  return (
    <section className="relative py-20 overflow-hidden bg-white">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-30" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-50 rounded-full blur-3xl opacity-30" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-block mb-4"
            whileHover={{ scale: 1.05 }}
          >
            <span className="px-6 py-2 bg-yellow-400 text-gray-900 font-semibold rounded-full text-sm">
              Why Choose Nava Properties
            </span>
          </motion.div>
          
          <h2 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4">
            Your Partner in
            <span className="block bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent mt-2">
              Wealth Creation
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We don't just sell properties—we build lasting wealth through strategic Dubai real estate investments.
          </p>
        </motion.div>

        {/* Reasons grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={index}
                className="group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <motion.div
                  className="h-full bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  {/* Icon */}
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mb-6 shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-800 mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-gray-600 text-lg mb-6">
            Ready to start your wealth creation journey?
          </p>
          <motion.a
            href="#contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-bold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            Schedule Free Consultation
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;